import React, {Dispatch, SetStateAction} from "react"
import {Customer, getCustomerName} from "../../resources/customer"
import {toNumber} from "lodash"
import {EditMode, RiskRate} from "../../resources/common"
import {Bank} from "../../resources/bank"
import {isUndefined} from "lodash"
import DescriptiveToggleSwitch from "../../components/DescriptiveToggleSwitch/DescriptiveToggleSwitch"
import Card from "../../containers/Card/Card"
import ApplicationsDefaultBankSelector from "../OrgSettings/ApplicationsDefaultBankSelector"
import {useIsUnitUser} from "../../services/auth"
import {AllowedProduct, AllowedProductLabels, AppVariantName, CheckDepositScannerVendor} from "../../resources/org"
import classNames from "classnames"
import ReactTooltip from "react-tooltip"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons"

interface Props {
	editMode: EditMode
	name: string
	setName: Dispatch<SetStateAction<string>>
	customerId: string | null
	setCustomerId: Dispatch<SetStateAction<string | null>>
	customers: Array<Customer>
	bankId?: string | null
	setBankId?: Dispatch<SetStateAction<string | null>>
	banks?: Array<Bank>
	bankNames?: string[]
	defaultBankName?: string
	setDefaultBankName?: Dispatch<SetStateAction<string | undefined>>
	orgInterchangeShare: number
	setOrgInterchangeShare: Dispatch<SetStateAction<number>>
	defaultCustomerRiskRate: RiskRate
	setDefaultCustomerRiskRate: Dispatch<SetStateAction<RiskRate>>
	twilioVerificationServiceId?: string
	setTwilioVerificationServiceId?: Dispatch<SetStateAction<string>>
	enableTwoFactorAuthentication: boolean
	setEnableTwoFactorAuthentication: Dispatch<SetStateAction<boolean>>
	enableApplePushProvisioning: boolean
	setEnableApplePushProvisioning: Dispatch<SetStateAction<boolean>>
	enableGooglePushProvisioning: boolean
	setEnableGooglePushProvisioning: Dispatch<SetStateAction<boolean>>
	supportWidgetId?: string | null
	setSupportWidgetId?: Dispatch<SetStateAction<string | undefined | null>>
	freshchatSupportWidgetId?: string | null
	setFreshchatSupportWidgetId?: Dispatch<SetStateAction<string | undefined | null>>
	allowedProducts: AllowedProduct[]
	setAllowedProducts: (value: AllowedProduct[]) => void
	checkDepositScannerVendor: CheckDepositScannerVendor
	setCheckDepositScannerVendor: Dispatch<SetStateAction<CheckDepositScannerVendor>>
	brandName?: string
	setBrandName?: Dispatch<SetStateAction<string | undefined>>
	appVariantName?: AppVariantName
	setAppVariantName?: Dispatch<SetStateAction<AppVariantName>>
}

export function OrgAttributes(props: Props) {
	const isUnitUser = useIsUnitUser()
	return (
		<div className="org-attributes">
			<Card title="Basic" className="org-attributes-section">
				<div className="field">
					<label className="label">Name</label>
					<div className="control">
						<input
							className="input"
							type="text"
							placeholder="Pied Piper"
							value={props.name}
							required
							disabled={props.editMode == EditMode.VIEW}
							onChange={(e) => props.setName(e.target.value)}
						/>
					</div>
				</div>
				{props.editMode == EditMode.NEW &&
				!isUndefined(props.bankId) &&
				!isUndefined(props.setBankId) &&
				!isUndefined(props.banks) ? (
					<div className="field">
						<label className="label">Bank</label>
						<div className="field-body">
							<div className="select">
								<select
									required
									value={props.bankId ? props.bankId : ""}
									onChange={(e) => (props.setBankId ? props.setBankId(e.target.value) : null)}
								>
									<option key="0" defaultValue=""></option>
									{props.banks.map((c) => (
										<option key={c.id} value={c.id}>
											{c.attributes.name}
										</option>
									))}
								</select>
							</div>
						</div>
					</div>
				) : null}
				<div className="field">
					<label className="label">Customer</label>
					<div className="field-body">
						<div className="select">
							<select
								value={props.customerId ? props.customerId : ""}
								onChange={(e) => props.setCustomerId(e.target.value)}
								disabled={props.editMode == EditMode.VIEW}
								required={props.editMode == EditMode.NEW}
							>
								<option key="0" defaultValue=""></option>
								{props.customers.map((c) => (
									<option key={c.id} value={c.id}>
										{`${c.id} - ${getCustomerName(c)}`}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>
				{props.editMode != EditMode.NEW &&
				!isUndefined(props.setTwilioVerificationServiceId) &&
				!isUndefined(props.twilioVerificationServiceId) ? (
					<div className="field">
						<label className="label">Twilio Verification Service Id</label>
						<div className="control">
							<input
								className="input"
								type="text"
								value={props.twilioVerificationServiceId}
								disabled={props.editMode == EditMode.VIEW}
								onChange={(e) =>
									props.setTwilioVerificationServiceId ? props.setTwilioVerificationServiceId(e.target.value) : null
								}
							/>
						</div>
					</div>
				) : null}

				<div className="field">
					<label className="label">Allowed Products</label>
					<div className="control">
						{Object.values(AllowedProduct).map((product) => {
							const isLockedFromEdit = !(props.editMode === EditMode.EDIT || props.editMode === EditMode.NEW)
							const shouldBeDisabled = isLockedFromEdit

							return (
								<label
									className={classNames("checkbox", "allowed-product-checkbox", {
										"is-disabled": shouldBeDisabled,
									})}
									key={product}
								>
									<input
										type="checkbox"
										checked={props.allowedProducts.includes(product)}
										disabled={shouldBeDisabled}
										onChange={(e) => {
											if (e.target.checked) {
												props.setAllowedProducts([...props.allowedProducts, product])
											} else {
												props.setAllowedProducts(props.allowedProducts.filter((p) => p !== product))
											}
										}}
									/>
									<span className="allowed-product-label">{AllowedProductLabels[product]}</span>
								</label>
							)
						})}
					</div>
				</div>

				<div className="field">
					<label className="label">Freshchat Support Widget ID</label>
					<div className="control">
						<input
							className="input"
							type="text"
							value={props.freshchatSupportWidgetId ?? undefined}
							disabled={props.editMode == EditMode.VIEW}
							onChange={(e) =>
								props.setFreshchatSupportWidgetId ? props.setFreshchatSupportWidgetId(e.target.value) : null
							}
						/>
					</div>
				</div>

				<div className="field">
					<label className="label">Zendesk Support Widget ID</label>
					<div className="control">
						<input
							className="input"
							type="text"
							value={props.supportWidgetId ?? undefined}
							disabled={props.editMode == EditMode.VIEW}
							onChange={(e) => (props.setSupportWidgetId ? props.setSupportWidgetId(e.target.value) : null)}
						/>
					</div>
				</div>

				<div className="field">
					<label className="label">Check Deposit Scanner Vendor</label>
					<div className="field-body">
						<div className="select">
							<select
								value={props.checkDepositScannerVendor}
								onChange={(e) => {
									const val = e.target.value
									if (val == CheckDepositScannerVendor.Mitek || val == CheckDepositScannerVendor.Veryfi) {
										props.setCheckDepositScannerVendor(val)
									}
								}}
								disabled={props.editMode == EditMode.VIEW}
							>
								<option value={CheckDepositScannerVendor.Mitek} key={CheckDepositScannerVendor.Mitek}>
									Mitek
								</option>
								<option value={CheckDepositScannerVendor.Veryfi} key={CheckDepositScannerVendor.Veryfi}>
									Veryfi
								</option>
							</select>
						</div>
					</div>
				</div>

				{props.editMode != EditMode.NEW && !isUndefined(props.setBrandName) ? (
					<div className="field">
						<label className="label">Brand Name</label>
						<div className="control">
							<input
								className="input"
								type="text"
								value={props.brandName ?? ""}
								disabled={props.editMode == EditMode.VIEW}
								onChange={(e) => (props.setBrandName ? props.setBrandName(e.target.value) : null)}
							/>
						</div>
					</div>
				) : null}

				{props.editMode != EditMode.NEW && !isUndefined(props.setAppVariantName) ? (
					<div className="field">
						<label className="label">App Variant Name</label>
						<div className="field-body">
							<div className="select">
								<select
									value={props.appVariantName ?? ""}
									onChange={(e) => {
										const val = e.target.value
										if ((Object.values(AppVariantName) as string[]).includes(val)) {
											props.setAppVariantName?.(val as AppVariantName)
										}
									}}
									disabled={props.editMode == EditMode.VIEW}
								>
									<option value={AppVariantName.Balance} key={AppVariantName.Balance}>
										Balance
									</option>
									<option value={AppVariantName.Bank} key={AppVariantName.Bank}>
										Bank
									</option>
									<option value={AppVariantName.Wallet} key={AppVariantName.Wallet}>
										Wallet
									</option>
								</select>
							</div>
						</div>
					</div>
				) : null}

				{props.editMode == EditMode.NEW ? (
					<div className="field">
						<label className="label">Org Interchange share</label>
						<div className="field-body">
							<div className="control">
								<input
									className="input"
									type="number"
									min="0"
									max="100"
									value={toNumber(props.orgInterchangeShare) * 100}
									pattern="^[1-9][0-9]?$|^100$|^0$"
									onChange={(e) => props.setOrgInterchangeShare(toNumber(e.target.value) / 100)}
								/>
							</div>
							<p className="control">
								<a className="button is-static">%</a>
							</p>
						</div>
					</div>
				) : null}
			</Card>
			<Card title="Applications" className="org-attributes-section">
				<div className="field">
					<label className="label">Default Risk Rate</label>
					<div className="field-body">
						<div className="select">
							<select
								value={props.defaultCustomerRiskRate}
								onChange={(e) => {
									const val = e.target.value

									if (val == RiskRate.Low || val == RiskRate.Medium || val == RiskRate.High) {
										props.setDefaultCustomerRiskRate(val)
									}
								}}
								disabled={props.editMode == EditMode.VIEW}
							>
								<option value={RiskRate.Low} key={RiskRate.Low}>
									Low
								</option>
								<option value={RiskRate.Medium} key={RiskRate.Medium}>
									Medium
								</option>
								<option value={RiskRate.High} key={RiskRate.High}>
									High
								</option>
							</select>
						</div>
					</div>
				</div>
				{props.editMode != EditMode.NEW && !isUndefined(props.setDefaultBankName) && isUnitUser ? (
					<ApplicationsDefaultBankSelector
						defaultBankName={props.defaultBankName}
						setDefaultBankName={props.setDefaultBankName}
						bankNames={props.bankNames}
						isDisabled={props.editMode == EditMode.VIEW}
					/>
				) : null}
			</Card>
			{isUnitUser && (
				<Card
					title={
						<>
							Enable Push Provisioning{" "}
							<label className="tooltip-container has-text-weight-normal">
								<a data-tip="React-tooltip" data-for="push-provisioning-tooltip" data-event="click focus">
									<FontAwesomeIcon icon={faInfoCircle} color="#545558" />
									<ReactTooltip
										id="push-provisioning-tooltip"
										place="right"
										className="tooltip-info"
										globalEventOff="click"
										border={true}
										borderColor="#C4C4C4"
										backgroundColor="#F5F5F5"
										textColor="black"
									>
										Enables the Add to Wallet web experience.
									</ReactTooltip>
								</a>
							</label>
						</>
					}
					className="org-attributes-section push-provisioning-container"
				>
					<div className="push-provisioning-toggles">
						<DescriptiveToggleSwitch
							text="Apple"
							checked={props.enableApplePushProvisioning}
							onChange={(e) => props.setEnableApplePushProvisioning(e.target.checked)}
							disabled={props.editMode == EditMode.VIEW}
						/>
						<DescriptiveToggleSwitch
							text="Google"
							checked={props.enableGooglePushProvisioning}
							onChange={(e) => props.setEnableGooglePushProvisioning(e.target.checked)}
							disabled={props.editMode == EditMode.VIEW}
						/>
					</div>
				</Card>
			)}
			<Card title="Security" className="org-attributes-section">
				<div className="field">
					<label className="label">Two-Factor Authentication</label>
					<div className="field-body">
						<DescriptiveToggleSwitch
							text={props.enableTwoFactorAuthentication ? "Enabled" : "Disabled"}
							description={
								props.enableTwoFactorAuthentication
									? "Activating 2FA is effective immediately. Org users who do not have a valid phone number will not be able to access the dashboard."
									: ""
							}
							checked={props.enableTwoFactorAuthentication}
							onChange={(e) => props.setEnableTwoFactorAuthentication(e.target.checked)}
							disabled={props.editMode == EditMode.VIEW}
						/>
					</div>
				</div>
			</Card>
		</div>
	)
}
